import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Search, Calendar, Clock, Tag, ChevronRight, 
  BookOpen, TrendingUp, Loader2 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { API } from '@/lib/api';
import QuickNav from '@/components/QuickNav';

const BlogPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState(null);
  const [tags, setTags] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadPosts();
    loadTags();
  }, [page, selectedTag]);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 9 });
      if (selectedTag) params.append('tag', selectedTag);
      
      const response = await axios.get(`${API}/blog/posts?${params}`);
      setPosts(response.data.posts);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTags = async () => {
    try {
      const response = await axios.get(`${API}/blog/tags`);
      setTags(response.data);
    } catch (error) {
      console.error('Error loading tags:', error);
    }
  };

  const filteredPosts = posts.filter(post =>
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const estimateReadTime = (content) => {
    const words = content?.split(/\s+/).length || 0;
    const minutes = Math.ceil(words / 200);
    return `${minutes} min de leitura`;
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-heavy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              className="text-gray-400 hover:text-white ml-12 sm:ml-14" 
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Início
            </Button>
            <div className="flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-[#00FF94]" />
              <span className="font-bold text-white">Blog</span>
            </div>
            <div className="w-20" />
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-32">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Dicas <span className="text-[#00FF94]">Financeiras</span>
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Conteúdos para te ajudar a tomar melhores decisões financeiras e evitar dívidas desnecessárias.
            </p>
          </motion.div>

          {/* Search and Filters */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              <Input
                placeholder="Buscar artigos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-[#0A0A0A] border-white/10 text-white"
              />
            </div>
          </motion.div>

          {/* Tags */}
          {tags.length > 0 && (
            <motion.div
              className="flex flex-wrap gap-2 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <button
                onClick={() => { setSelectedTag(null); setPage(1); }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  !selectedTag 
                    ? 'bg-[#00FF94] text-black' 
                    : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
              >
                Todos
              </button>
              {tags.map((tag) => (
                <button
                  key={tag.tag}
                  onClick={() => { setSelectedTag(tag.tag); setPage(1); }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedTag === tag.tag 
                      ? 'bg-[#00FF94] text-black' 
                      : 'bg-white/5 text-gray-400 hover:bg-white/10'
                  }`}
                >
                  {tag.tag} ({tag.count})
                </button>
              ))}
            </motion.div>
          )}

          {/* Posts Grid */}
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#00FF94] animate-spin" />
            </div>
          ) : filteredPosts.length === 0 ? (
            <motion.div
              className="text-center py-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <BookOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Nenhum artigo encontrado</h3>
              <p className="text-gray-400">
                {searchTerm 
                  ? 'Tente buscar com outros termos' 
                  : 'Em breve teremos novos conteúdos!'}
              </p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPosts.map((post, index) => (
                <motion.article
                  key={post.post_id}
                  className="group bg-[#0A0A0A] border border-white/10 rounded-2xl overflow-hidden hover:border-[#00FF94]/30 transition-all"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.05 }}
                >
                  <Link to={`/blog/${post.slug}`}>
                    {/* Cover Image */}
                    {post.cover_image ? (
                      <div className="aspect-video bg-gray-900 overflow-hidden">
                        <img 
                          src={post.cover_image} 
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    ) : (
                      <div className="aspect-video bg-gradient-to-br from-[#00FF94]/20 to-emerald-500/10 flex items-center justify-center">
                        <BookOpen className="w-12 h-12 text-[#00FF94]/50" />
                      </div>
                    )}
                    
                    {/* Content */}
                    <div className="p-5">
                      {/* Tags */}
                      {post.tags && post.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {post.tags.slice(0, 2).map((tag) => (
                            <span 
                              key={tag} 
                              className="text-xs px-2 py-1 bg-[#00FF94]/10 text-[#00FF94] rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {/* Title */}
                      <h2 className="text-lg font-bold text-white mb-2 group-hover:text-[#00FF94] transition-colors line-clamp-2">
                        {post.title}
                      </h2>
                      
                      {/* Excerpt */}
                      <p className="text-sm text-gray-400 mb-4 line-clamp-2">
                        {post.excerpt}
                      </p>
                      
                      {/* Meta */}
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          <span>{formatDate(post.published_at)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          <span>{estimateReadTime(post.content)}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <motion.div
              className="flex justify-center gap-2 mt-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <Button
                variant="outline"
                className="border-white/10 text-gray-400 hover:bg-white/5"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Anterior
              </Button>
              <span className="flex items-center px-4 text-gray-400">
                Página {page} de {totalPages}
              </span>
              <Button
                variant="outline"
                className="border-white/10 text-gray-400 hover:bg-white/5"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Próxima
              </Button>
            </motion.div>
          )}

          {/* CTA Section */}
          <motion.div
            className="mt-16 p-8 bg-gradient-to-r from-[#00FF94]/10 to-emerald-500/10 border border-[#00FF94]/20 rounded-2xl text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <TrendingUp className="w-12 h-12 text-[#00FF94] mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">
              Organize suas finanças agora
            </h3>
            <p className="text-gray-400 mb-6 max-w-lg mx-auto">
              Além de ler sobre finanças, coloque em prática! Use nosso organizador de despesas gratuito.
            </p>
            <Button
              className="bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-full font-bold px-8"
              onClick={() => navigate('/expenses')}
            >
              Começar a organizar <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>

          {/* Social Follow Section */}
          <motion.div
            className="mt-8 p-6 bg-[#0A0A0A] border border-white/5 rounded-xl flex flex-col sm:flex-row items-center justify-between gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
          >
            <p className="text-sm text-gray-400">
              Siga-nos para mais dicas financeiras:
            </p>
            <div className="flex items-center gap-3">
              <a
                href="https://www.instagram.com/evite_dividas_oficial"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#E1306C]/10 border border-[#E1306C]/20 text-[#E1306C] hover:bg-[#E1306C]/20 transition-colors text-sm font-medium"
                data-testid="blog-follow-instagram"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                Instagram
              </a>
              <a
                href="https://br.pinterest.com/evitedividas/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#E60023]/10 border border-[#E60023]/20 text-[#E60023] hover:bg-[#E60023]/20 transition-colors text-sm font-medium"
                data-testid="blog-follow-pinterest"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641 0 12.017 0z"/></svg>
                Pinterest
              </a>
            </div>
          </motion.div>
        </div>
      </main>

      <QuickNav />
    </div>
  );
};

export default BlogPage;
